import { DownOutlined, RightOutlined } from '@ant-design/icons'
import type { Node, NodeProps } from '@xyflow/react'
import { useContext } from 'react'
import type { GroupNodeData } from './auditLayout'
import { AuditToggleContext } from './auditToggleContext'

export type AuditGroupNodeType = Node<GroupNodeData, 'auditGroup'>

export function AuditGroupNode({ data }: NodeProps<AuditGroupNodeType>) {
  const {
    groupKey,
    title,
    technicalToken,
    depth,
    fieldCount,
    errorCount,
    warnCount,
    showTechnical,
    collapsed,
    separators,
  } = data
  const toggle = useContext(AuditToggleContext)
  const indent = depth * 22
  const accent =
    errorCount > 0
      ? 'var(--theme-error)'
      : warnCount > 0
        ? 'var(--theme-warning)'
        : 'transparent'

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background:
          depth > 0 ? 'hsl(var(--muted) / 0.35)' : 'hsl(var(--muted) / 0.18)',
        border: '1px solid hsl(var(--border))',
        borderLeft:
          accent === 'transparent'
            ? '1px solid hsl(var(--border))'
            : `3px solid ${accent}`,
        borderRadius: 'var(--radius)',
        boxSizing: 'border-box',
      }}
    >
      {separators.map((y) => (
        <div
          key={y}
          style={{
            position: 'absolute',
            left: 14,
            right: 14,
            top: y,
            height: 1,
            background: 'hsl(var(--border))',
          }}
        />
      ))}
      <div
        onClick={() => toggle(groupKey)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 40,
          padding: `0 14px 0 ${14 + indent}px`,
          borderBottom: collapsed ? 'none' : '1px solid hsl(var(--border))',
          cursor: 'pointer',
          userSelect: 'none',
          // re-enable events so the header is clickable while the node wrapper
          // is pointer-events:none (canvas is locked)
          pointerEvents: 'auto',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            fontSize: 10,
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          {collapsed ? <RightOutlined /> : <DownOutlined />}
        </span>
        {showTechnical ? (
          <span
            style={{
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: 11,
              fontWeight: 500,
              color: 'hsl(var(--primary))',
              background: 'hsl(var(--primary) / 0.12)',
              padding: '2px 8px',
              borderRadius: 4,
            }}
          >
            {technicalToken}
          </span>
        ) : null}
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'hsl(var(--foreground))',
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>
          {fieldCount} {fieldCount === 1 ? 'field' : 'fields'}
        </span>
        <div style={{ flex: 1 }} />
        {errorCount > 0 ? (
          <Badge
            color='var(--theme-error)'
            text={`${errorCount} ${errorCount === 1 ? 'error' : 'errors'}`}
          />
        ) : warnCount > 0 ? (
          <Badge
            color='var(--theme-warning)'
            text={`${warnCount} ${warnCount === 1 ? 'warning' : 'warnings'}`}
          />
        ) : null}
      </div>
    </div>
  )
}

function Badge({ color, text }: { color: string; text: string }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: '#fff',
        background: color,
        borderRadius: 9,
        padding: '1px 9px',
      }}
    >
      {text}
    </span>
  )
}
